import { Component, OnInit, inject, computed, signal, output, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoModule } from '@jsverse/transloco';
import { debounceTime } from 'rxjs';
import { InternalLinkSearchResult } from 'shared/domain';
import {
  FormFieldComponent,
  IconDirective,
  InputDirective,
  PopoverRef,
  SpinnerComponent,
  TooltipDirective,
  ButtonDirective,
} from 'shared/ui-common';

import { LinkElementType, GuidelineBase, Guideline, POPOVER_DATA } from '@treetop/shared/model';

import { AnchorLinkSearchPopoverGuidelineComponent } from './anchor-links-search-popover-guideline/anchor-link-search-popover-guideline.component';
import { AnchorLinksSearchPopoverService } from './anchor-links-search-popover.service';

export type AnchorLinkPopoverData = {
  isTextEditor?: boolean;
  guideline?: Guideline;
  searchFormControl?: FormControl;
};

@Component({
  standalone: true,
  imports: [
    MatProgressSpinnerModule,
    MatTooltipModule,
    TranslocoModule,
    MatDividerModule,
    MatInputModule,
    MatFormFieldModule,
    ReactiveFormsModule,

    MatIconModule,
    IconDirective,
    TooltipDirective,
    SpinnerComponent,
    AnchorLinkSearchPopoverGuidelineComponent,
    FormFieldComponent,
    InputDirective,
    FormsModule,
    ButtonDirective,
  ],
  selector: 'tt-anchor-links-search-popover',
  templateUrl: './anchor-links-search-popover.component.html',
  styleUrls: ['./anchor-links-search-popover.component.scss'],
  providers: [AnchorLinksSearchPopoverService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnchorLinksSearchPopoverComponent implements OnInit {
  private service = inject(AnchorLinksSearchPopoverService);

  protected data? = inject<AnchorLinkPopoverData>(POPOVER_DATA, { optional: true });
  private popoverRef? = inject(PopoverRef, { optional: true });

  public linkSelected = output<InternalLinkSearchResult>();

  protected selectedGuideline = this.service.selectedGuideline;
  protected searchLinkResults = this.service.searchLinkResults;
  protected searchGuidelineResults = this.service.searchGuidelineResults;
  protected loading = this.service.loading;

  protected searchTerm = signal('');

  protected hasResults = computed(() => {
    return this.searchLinkResults().length > 0 || this.searchGuidelineResults().length > 0;
  });

  public ngOnInit(): void {
    this.service.init({
      guideline: this.data?.guideline,
    });

    this.service.searchLinks(this.data?.searchFormControl?.value || '');

    this.data?.searchFormControl?.valueChanges.pipe(debounceTime(500)).subscribe((searchTerm) => {
      this.handleSearchTermChange(searchTerm);
    });
  }

  protected handleSearchTermChange(searchTerm: string) {
    this.service.searchLinks(searchTerm);
  }

  protected linkToGuidelineSection(guideline: GuidelineBase) {
    this.service.getGuidelineDetails(guideline.uniqueId);
  }

  public selectLink(link: InternalLinkSearchResult): void {
    this.linkSelected.emit(link);
    this.popoverRef?.close(link);
  }

  protected clearSelectedGuideline() {
    this.service.clearSelectedGuideline();
  }

  protected LinkElementType = LinkElementType;
}
